import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Building, Shield, Briefcase, Target, ArrowRight, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../mockData';

const iconMap = { Building, Shield, Briefcase, Target };

const ServicesPage = () => {
  const { t } = useTranslation();
  const [heroSlide, setHeroSlide] = useState(0);
  
  // Hero carousel images
  const heroSlides = [
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&q=90", // Business team
    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=90", // Consulting
    mockData.drcImages.city1, // DRC city
    "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&q=90"  // Business solutions
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  const serviceDetails = [
    {
      ...mockData.services[0],
      features: [
        t('services.feature1_1'),
        t('services.feature1_2'),
        t('services.feature1_3'),
        t('services.feature1_4')
      ]
    },
    {
      ...mockData.services[1],
      features: [
        t('services.feature2_1'),
        t('services.feature2_2'),
        t('services.feature2_3'),
        t('services.feature2_4')
      ]
    },
    {
      ...mockData.services[2],
      features: [
        t('services.feature3_1'),
        t('services.feature3_2'),
        t('services.feature3_3'),
        t('services.feature3_4')
      ]
    },
    {
      ...mockData.services[3],
      features: [
        t('services.feature4_1'),
        t('services.feature4_2'),
        t('services.feature4_3'),
        t('services.feature4_4')
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Premium Hero Section with Carousel */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[500px]">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={heroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[heroSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-blue-800/60"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-6 py-2 mb-6">
              {t('services.title')}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{t('services.title')}</h1>
            <p className="text-2xl text-blue-100 font-light">{t('services.subtitle')}</p>
            
            {/* Slide Indicators */}
            <div className="flex gap-2 justify-center pt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === heroSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10">
            {serviceDetails.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group">
                  <CardHeader className="pb-6">
                    <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-5 rounded-2xl w-20 h-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-10 h-10 text-blue-900" />
                    </div>
                    <CardTitle className="text-3xl text-blue-900 group-hover:text-blue-700 transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <CardDescription className="text-gray-700 text-lg leading-relaxed">
                      {service.description}
                    </CardDescription>
                    <div className="space-y-3">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/contact">
                      <Button className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white py-6 text-lg mt-4">
                        {t('common.learnMore')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-20 bg-gradient-to-br from-blue-50 to-white p-12 rounded-3xl border-2 border-blue-100">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('services.customSolution')}</h2>
              <p className="text-xl text-gray-600 mb-8">{t('services.tailoredServices')}</p>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white px-12 py-7 text-lg">
                  {t('services.contactTeam')} <ArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
